import React, { useRef, useState, KeyboardEvent, useEffect } from 'react';
import { Wrapper, Header, ColumnLeft, ColumnRight, IconWrapper, Icon, TextContainer, MainText, SubText, EditButton, CancelButton, Body, BodyText, ButtonsWrapper, IconContainer } from './styled';
import { Button } from 'elements';

import InputAreaAI from "components/InputAreaAI";
import { DataType } from "components/InputAreaAI/components/Assistant/data";

// @ts-ignore
import rewardsIcon from "assets/images/journeys/purpose/rewards.svg";

// @ts-ignore
import consequencesIcon from "assets/images/journeys/purpose/consequences.svg";

// @ts-ignore
import obstaclesIcon from "assets/images/journeys/purpose/obstacles.svg";

// @ts-ignore
import solutionsIcon from "assets/images/journeys/purpose/solutions.svg";

export enum ItemType {
  Rewards = 'rewards',
  Consequences = 'consequences',
  Obstacles = 'obstacles',
  Solutions = 'solutions'
}

interface ItemProps {
  type: ItemType;
  text?: string;
  onChange?: (type: ItemType, value: string, callback?: () => void) => void;
  area?: string;       // Area name from parent component
  category?: string;   // Category name from parent component
  goal_name?: string;  // Goal name from parent component
  goal_id?: string;    // Goal ID from parent component
}

const Item: React.FC<ItemProps> = ({ type, text, onChange, area, category, goal_name, goal_id }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(text || '');
  const [isSaving, setIsSaving] = useState(false);

  const $inputRef = useRef(null);
  
  // Reset editedText and isEditing state when text prop changes or when switching to a different goal
  useEffect(() => {
    setEditedText(text || '');
    setIsEditing(false);
    setIsSaving(false);
  }, [text, goal_id]);

  const getMainText = () => {
    switch (type) {
      case ItemType.Rewards:
        return "If I Do";
      case ItemType.Consequences:
        return "If I Don't";
      case ItemType.Obstacles:
        return "What's Blocking Me";
      case ItemType.Solutions:
        return "How I'll Do It";
      default:
        return "If I do";
    }
  };

  const getSubText = () => {
    switch (type) {
      case ItemType.Rewards:
        return "Rewards";
      case ItemType.Consequences:
        return "Consequences";
      case ItemType.Obstacles:
        return "Obstacles";
      case ItemType.Solutions:
        return "Solutions";
      default:
        return "Rewards";
    }
  };

  const getIconSrc = () => {
    switch (type) {
      case ItemType.Rewards:
        return rewardsIcon;
      case ItemType.Consequences:
        return consequencesIcon;
      case ItemType.Obstacles:
        return obstaclesIcon;
      case ItemType.Solutions:
        return solutionsIcon;
      default:
        return rewardsIcon;
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);

    setTimeout(() => {
      // @ts-ignore
      $inputRef.current?.focus();
    }, 50);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setEditedText(text || '');
  };

  const handleSaveClick = () => {
    setIsSaving(true);

    if (onChange) {
      onChange(type, editedText.trim(), () => {
        // Only hide the textarea after the API call is successful
        setIsEditing(false);
        setIsSaving(false);
        setEditedText(editedText.trim());
      });
    }
  };

  const handleInputChange = (value: string) => {
    setEditedText(value);
  };

  const handleInputKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveClick();
    }
  };

  return (
    <Wrapper type={type} isEditing={isEditing}>
      <Header>
        <ColumnLeft>
          <IconWrapper type={type}>
            <Icon src={getIconSrc()} alt={`${type} icon`} />
          </IconWrapper>
          <TextContainer>
            <MainText type={type}>{getSubText()}</MainText>
            <SubText>{getMainText()}</SubText>
          </TextContainer>
        </ColumnLeft>
        <ColumnRight>
          {isEditing ? (
            <ButtonsWrapper>
              <CancelButton
                onClick={handleCancelClick}
                disabled={isSaving}
              >
                Cancel
              </CancelButton>
              <Button
                styleType="primary"
                size="small"
                version="new"
                onClick={handleSaveClick}
                disabled={editedText === (text || '') || isSaving}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </ButtonsWrapper>
          ) : (
            <EditButton onClick={handleEditClick}>Edit</EditButton>
          )}
        </ColumnRight>
      </Header>
      <Body isEditing={isEditing}>
        {isEditing ? (
          <InputAreaAI
            id={`input-${type}`}
            value={editedText}
            name={type}
            placeholder={`Enter ${getSubText()}`}
            style={{ padding: '10px 34px 10px 16px', fontSize: 16, height: 42, wordBreak: 'break-word', maxHeight: 180 }}
            max={255}
            ref={$inputRef}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            disabled={isSaving}
            assistant={{
              type: type === ItemType.Rewards ? DataType.REWARD :
                    type === ItemType.Consequences ? DataType.CONSEQUENCE :
                    type === ItemType.Obstacles ? DataType.OBSTACLES :
                    type === ItemType.Solutions ? DataType.SOLUTIONS : DataType.ACTION,
              title: getSubText(),
              order: "1",
              area: area || String(type),
              category: category || getSubText(),
              goal: goal_name,
              prompt: editedText
            }}
          />
        ) : (
          <BodyText>
            {text}
          </BodyText>
        )}
      </Body>
    </Wrapper>
  );
}

export default Item;